<template>
  <div class="fixed inset-0 z-50 overflow-y-auto">
    <!-- 遮罩层 -->
    <div class="fixed inset-0 bg-black bg-opacity-50" @click="handleClose"></div>

    <!-- 弹窗内容 -->
    <div class="flex min-h-full items-center justify-center p-4">
      <div class="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg">
        <!-- 头部 -->
        <div class="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
            话题定制 - {{ member.topicName || `话题 #${member.topicId}` }}
          </h3>
          <button @click="handleClose" class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300">
            <XMarkIcon class="w-6 h-6" />
          </button>
        </div>

        <!-- Part 2 问题参考 -->
        <div v-if="member.part2Question"
          class="mx-4 mt-4 p-3 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-md">
          <div class="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <span>📋</span>
            <span>Part 2 问题</span>
          </div>
          <p class="text-sm text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-line">
            {{ member.part2Question }}
          </p>
        </div>
        <div v-else
          class="mx-4 mt-4 p-3 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-md">
          <p class="text-sm text-gray-400 dark:text-gray-500 italic">暂无 Part 2 问题</p>
        </div>

        <!-- 表单内容 -->
        <div class="p-4 space-y-4">
          <!-- 开场白 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              开场白 (Intro)
            </label>
            <textarea v-model="form.intro" rows="3" placeholder="针对此话题的开场白..."
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"></textarea>
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
              用于在串题答案中引入此话题时的过渡语
            </p>
          </div>

          <!-- 结束语 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              结束语 (Outro)
            </label>
            <textarea v-model="form.outro" rows="3" placeholder="针对此话题的结束语..."
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"></textarea>
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
              用于在串题答案中结束此话题时的过渡语
            </p>
          </div>

          <!-- 备注 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              备注 (Notes)
            </label>
            <textarea v-model="form.notes" rows="3" placeholder="其他备注信息..."
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"></textarea>
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
              关于此话题在串题中的特殊说明
            </p>
          </div>
        </div>

        <!-- 底部 -->
        <div class="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
          <button @click="handleClose"
            class="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors">
            取消
          </button>
          <button @click="handleSave"
            class="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors">
            保存
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, onMounted } from 'vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import type { TopicGroupMember } from '@/types/topic/topicGroup'

/**
 * Props
 */
const props = defineProps<{
  /** 成员信息 */
  member: TopicGroupMember
}>()

/**
 * Emits
 */
const emit = defineEmits<{
  (e: 'close'): void
  (e: 'save', data: { intro?: string; outro?: string; notes?: string }): void
}>()

// 表单
const form = reactive({
  intro: '',
  outro: '',
  notes: '',
})

/**
 * 关闭弹窗
 */
const handleClose = () => {
  emit('close')
}

/**
 * 保存
 */
const handleSave = () => {
  emit('save', {
    intro: form.intro || undefined,
    outro: form.outro || undefined,
    notes: form.notes || undefined,
  })
}

// 组件挂载时初始化表单
onMounted(() => {
  if (props.member.note) {
    form.intro = props.member.note.intro || ''
    form.outro = props.member.note.outro || ''
    form.notes = props.member.note.notes || ''
  }
})
</script>
